/**
 * useLooseRecording.ts — Thin composable for free-form recording on the Practice page.
 *
 * Wires the shared useRecorder (RecorderService, VAD events, upload dialog) to usePracticeUpload
 * with no FSM. Adds only what is specific to loose recording: the isRecording flag, phrase-split
 * blips, sentence focus points from phrase clicks, and pausing while the teacher's audio plays.
 */
import type { AliyahType, HaftarahType, SentenceGroupType, SentenceType } from 'types/types'
import { computed, type ComputedRef, onBeforeUnmount, type Ref, ref, watch } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { formatPracticeDescription } from '@/composables/format'
import { ReadOrder } from '@/composables/usePracticeFSM'
import { usePracticeUpload } from '@/composables/usePracticeUpload'
import { createRecorderState, useRecorder } from '@/composables/useRecorder'
import { DEFAULT_SILENCE_PATIENCE, PHRASE_SPLIT_BLIP_MS, VOICE_SENSITIVITY_DEFAULT } from '@/constants/practiceConstants'
import store from '@/store'
import type { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { VoiceStyleSet } from '@/util/VoiceStyles'

export interface LooseRecordingDeps {
  aliyah: ComputedRef<AliyahType | null>
  haftarah: ComputedRef<HaftarahType | null>
  playing: Ref<boolean>
  recordingsInStyle: ComputedRef<Record<string, Array<SentenceRecordingMetadataWithPitchedAudios>>>
  sentences: ComputedRef<SentenceType[]>
  sentenceGroup: ComputedRef<SentenceGroupType | null>
  voiceStyleSet: Ref<VoiceStyleSet>
}

export function useLooseRecording(logger: VueLogger, deps: LooseRecordingDeps) {
  // {{{ internal state
  const recorderState = createRecorderState()
  const { focusPoints, recordingStartedAt, segmentPoints, uploadRecordingOverlay, uploading, voiceEvents } = recorderState

  const isRecording = ref(false)
  const focusSentenceIndex = ref<number | null>(null)

  // Phrase-split silence detection: briefly pulses true when the VAD confirms
  // voice has stopped — meaning silence exceeded the flip resistance (~800ms),
  // well past the backend's phrase-splitting gap threshold.
  const phraseSplitDetected = ref(false)
  let blipTimer: ReturnType<typeof setTimeout> | null = null

  function triggerPhraseSplitBlip() {
    if (blipTimer) {
      clearTimeout(blipTimer)
    }
    phraseSplitDetected.value = true
    blipTimer = setTimeout(() => {
      blipTimer = null
      phraseSplitDetected.value = false
    }, PHRASE_SPLIT_BLIP_MS)
  }

  /** Close any open voice-active period with a synthetic voiceStop.
   *  Called before teacher playback (to avoid dead-air gaps) and when
   *  recording stops mid-voice (to avoid losing the final segment). */
  function closeOpenVoiceEvent(reason: string) {
    const events = voiceEvents.value
    if (!events.length) {
      return
    }
    const last = events[events.length - 1]
    if (last.voiceStartAt !== 0 && last.voiceStopAt === 0) {
      const ts = Date.now() - recordingStartedAt.value
      console.log(`[looseRecording] ${reason} → synthetic voiceStop at ${ts}ms`)
      voiceEvents.value.push({ voiceStartAt: 0, voiceStopAt: ts })
    }
  }
  // }}}

  // {{{ practice description
  const practiceDescription = computed<string>(() =>
    formatPracticeDescription(deps.aliyah.value, deps.haftarah.value, deps.sentenceGroup.value),
  )
  // }}}

  // {{{ usePracticeUpload wiring
  const readOrder = ref(ReadOrder.STUDENT_ONLY)
  const sentence = computed(() => deps.sentences.value[focusSentenceIndex.value ?? 0] ?? null)

  // Look up the teacher's recording metadata for the current focus sentence.
  // This provides segmentedSentence.id for focus points, which PlaybackOverlay
  // needs to display phrased segmentation during playback.
  const focusSentenceRecordingMetadata = computed(() => {
    const s = sentence.value
    if (!s) {
      return null
    }
    const recs = deps.recordingsInStyle.value[s.id]
    return recs?.[0] ?? null
  })

  const practiceUpload = usePracticeUpload(logger, {
    aliyah: deps.aliyah as Ref<AliyahType | null>,
    focusPoints,
    focusSentenceIndex,
    focusSentenceRecordingMetadataWithPitchedAudios: focusSentenceRecordingMetadata,
    haftarah: deps.haftarah as Ref<HaftarahType | null>,
    readOrder,
    recordingStartedAt,
    segmentPoints,
    sentence,
    sentenceGroup: deps.sentenceGroup as Ref<SentenceGroupType | null>,
    store,
    uploadRecordingOverlay,
    uploading,
    voiceEvents,
    voiceStyleSet: deps.voiceStyleSet,
  })
  // }}}

  // {{{ recorder
  const recorder = useRecorder(logger, {
    onRecordingStarting: () => {
      isRecording.value = true
      // Default to sentence 0 so the upload guard in uploadPractice() passes even if the student never
      // clicks a specific sentence during recording. The initial focus point (pushed by useRecorder)
      // gives PlaybackOverlay text to show.
      focusSentenceIndex.value = 0
    },
    onVoiceStart: () => {
      console.log(`[looseRecording] onVoiceStart, total events: ${voiceEvents.value.length}`)
    },
    onVoiceStop: () => {
      console.log(`[looseRecording] onVoiceStop, total events: ${voiceEvents.value.length}`)
      triggerPhraseSplitBlip()
    },
    practiceUpload,
    state: recorderState,
    vadSettings: () => ({ sensitivity: VOICE_SENSITIVITY_DEFAULT, silencePatience: DEFAULT_SILENCE_PATIENCE }),
  })
  // }}}

  // {{{ pause/unpause during teacher playback
  watch(
    () => deps.playing.value,
    (isPlaying) => {
      const recorderService = recorderState.recorderService.value
      if (!recorderService || !isRecording.value) {
        return
      }
      if (isPlaying) {
        closeOpenVoiceEvent('teacher playing')
        console.log('[looseRecording] teacher playing → pausing recorder')
        recorderService.pause()
      } else {
        console.log('[looseRecording] teacher stopped → unpausing recorder')
        recorderService.unpause()
      }
    },
  )
  // }}}

  // {{{ toggleRecord
  function toggleRecord() {
    if (isRecording.value || recorderState.backgroundSampling.value) {
      if (isRecording.value) {
        closeOpenVoiceEvent('recording stopped')
      }
      recorder.stopRecorder()
      isRecording.value = false
    } else {
      recorder.startRecorder()
    }
  }
  // }}}

  // {{{ pushSentenceFocusPoint — called by PracticeListenTextView on segment click during recording
  function pushSentenceFocusPoint(sentenceIndex: number, segmentIndex: number) {
    if (!isRecording.value) {
      return
    }
    console.log(
      `[looseRecording] pushSentenceFocusPoint sentence=${sentenceIndex} segment=${segmentIndex} focusPoints=${focusPoints.value.length}`,
    )
    focusSentenceIndex.value = sentenceIndex
    practiceUpload.pushFocusPoint()
    practiceUpload.pushSegmentPoint(segmentIndex)
  }
  // }}}

  onBeforeUnmount(() => {
    if (blipTimer) {
      clearTimeout(blipTimer)
      blipTimer = null
    }
  })

  return {
    // Read-only state — only the composable controls these
    backgroundSampling: computed(() => recorderState.backgroundSampling.value),
    backgroundSamplingPercent: computed(() => recorderState.backgroundSamplingPercent.value),
    discardRecording: recorder.discardRecording,
    isRecording: computed(() => isRecording.value),
    pendingRecordingBlobUrl: computed(() => recorderState.pendingRecordingBlobUrl.value),
    phraseSplitDetected: computed(() => phraseSplitDetected.value),
    practiceDescription,
    pushSentenceFocusPoint,
    saveRecording: recorder.saveRecording,
    shareRecording: recorder.shareRecording,
    toggleRecord,
    uploadRecordingOverlay: computed(() => uploadRecordingOverlay.value),
    uploading: computed(() => uploading.value),
  }
}
